import { useRef } from "react";
import { ContourField } from "@mineoptics/brand/canvas/ContourField";
import { PillLink } from "@/components/PillLink";
import { gsap, useGSAP } from "@/lib/gsap";
import { useReducedMotion } from "@/lib/motion";
import { SIGNUP_URL } from "@/lib/site";

const INDEX = ["Sites & drillholes", "Drill & blast", "Load & haul", "Processing", "Scenarios"];

export function Hero() {
  const root = useRef<HTMLElement>(null);
  const reduced = useReducedMotion();

  useGSAP(
    () => {
      if (reduced) return;
      const scrollTrigger = { trigger: root.current, start: "top top", end: "bottom top", scrub: true };
      gsap.to("[data-hero-content]", { yPercent: -14, autoAlpha: 0, ease: "none", scrollTrigger });
      gsap.to("[data-hero-canvas]", { scale: 1.2, ease: "none", scrollTrigger });
    },
    { scope: root, dependencies: [reduced] },
  );

  return (
    <section ref={root} data-theme="dark" className="relative flex min-h-[100svh] flex-col justify-end overflow-hidden bg-bg">
      <div data-hero-canvas className="absolute inset-0">
        <ContourField className="h-full w-full" />
      </div>
      {/* Keep the headline legible over the contours and blend into the next section. */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_90%_at_0%_100%,rgba(9,11,10,0.92)_0%,rgba(9,11,10,0.55)_38%,transparent_70%)]" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-bg to-transparent" />

      <div data-hero-content className="shell relative pb-8 pt-36 md:pb-10">
        <p data-fade data-now data-delay="0.1" className="eyebrow eyebrow-mark mb-6 text-mute md:mb-8">
          Cost modelling for open-pit studies
        </p>

        <h1 data-split data-now data-delay="0.15" className="display max-w-[12ch]">
          Every tonne, accounted for.
        </h1>

        <div className="mt-8 flex flex-col gap-8 md:mt-12 md:flex-row md:items-end md:justify-between">
          <p data-fade data-now data-delay="0.55" className="lede max-w-[46ch] text-fg/80">
            MineOptics takes an open-pit project from drillhole data to a costed scenario you can issue as a report,
            then holds the operation against it. Mine plan, fleet, plant and overheads in one place, with every
            formula published.
          </p>
          <div data-fade data-now data-delay="0.7" className="flex flex-wrap items-center gap-3">
            <PillLink to={SIGNUP_URL} label="Start free" variant="accent" arrow />
            <PillLink to="/#platform" label="See how it works" variant="outline" />
          </div>
        </div>

        <div data-fade data-now data-delay="0.9" className="mt-14 hidden md:block">
          <span className="rule" />
          <ol className="mt-4 grid grid-cols-5 gap-6">
            {INDEX.map((item, index) => (
              <li key={item} className="eyebrow flex gap-3 text-mute">
                <span className="text-fg/40">0{index + 1}</span>
                {item}
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}
